import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common'
import { Component, inject, signal } from '@angular/core'
import { Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { MessageService } from 'primeng/api'
import { Button } from 'primeng/button'
import { Tag } from 'primeng/tag'

import { Action, BillingAccount, Resource } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { CheckAccessDirective } from '@ti/core/common/directives'
import { BrowserSessionStorage } from '@ti/core/common/storage'
import { injectDestroy$, selectState } from '@ti/core/common/utils'
import { OrderByData } from '@ti/core/contracts/data-grid'
import { FileSearchIconComponent } from '@ti/core/icons/file-search'
import { FleetsCountIconComponent } from '@ti/core/icons/fleets-count'
import { NoSearchIconComponent } from '@ti/core/icons/no-search'
import { PlaySolidReverseIconComponent } from '@ti/core/icons/play-solid-reverse'
import { DateFormatService, LanguageService } from '@ti/core/ui-kit/i18n'
import { DataGridComponent } from '@ti/core/ui-kit/layout/components/data-grid'
import { FleetHeaderComponent } from '@ti/core/ui-kit/layout/components/fleet-header'

import { EditAccountDialogComponent } from '../../../account/components/edit/edit-account-dialog.component'
import { BillingAccountsModel } from '../../models/billing-accounts.model'

@Component({
  selector: 'app-ti-billing-accounts-list',
  templateUrl: 'billing-accounts-list.component.html',
  styleUrls: ['billing-accounts-list.component.scss'],
  imports: [
    EditAccountDialogComponent,
    PlaySolidReverseIconComponent,
    Button,
    CheckAccessDirective,
    TranslatePipe,
    DecimalPipe,
    NoSearchIconComponent,
    FileSearchIconComponent,
    Tag,
    NgClass,
    DataGridComponent,
    AsyncPipe,
    FleetHeaderComponent,
    FleetsCountIconComponent,
  ],
})
export class BillingAccountsListComponent {
  // State
  protected readonly model = inject(BillingAccountsModel)
  protected readonly state = selectState(BillingAccountsModel)
  // First day of the previous completed month in local timezone
  protected readonly previousCompletedMonth = signal<Date>(
    (() => {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth() - 1, 1)
    })(),
  )
  protected readonly processingAccount = signal<number | null>(null)
  protected readonly destroy$ = injectDestroy$()

  // Dependencies
  protected readonly api = inject(ApiService)
  protected readonly router = inject(Router)
  protected readonly languageService = inject(LanguageService)
  protected readonly messageService = inject(MessageService)
  protected readonly sessionStorage = inject(BrowserSessionStorage)
  protected readonly dateFormatService = inject(DateFormatService)

  // Enums
  protected readonly Action = Action
  protected readonly Resource = Resource

  protected onSortUpdated(event: OrderByData) {
    this.model.setOrder(event)
  }

  protected onLoadMoreClicked() {
    this.model.loadNextPage()
  }

  protected onNameClicked(tspId: number) {
    return this.router.navigate(['/billing', tspId])
  }

  protected onLastBillClicked(tspId: number) {
    const previousCompletedMonth = this.previousCompletedMonth()
    return this.router.navigate([
      '/billing',
      tspId,
      previousCompletedMonth.getFullYear(),
      previousCompletedMonth.getMonth() + 1,
    ])
  }

  protected async activate(account: BillingAccount) {
    if (this.isProcessing(account.id)) {
      return
    }
    this.processingAccount.set(account.id)
    await this.model.toggleActiveStatus(account, true)
    this.processingAccount.set(null)
  }

  protected async suspend(account: BillingAccount) {
    if (this.isProcessing(account.id)) {
      return
    }
    this.processingAccount.set(account.id)
    await this.model.toggleActiveStatus(account, false)
    this.processingAccount.set(null)
  }

  protected isProcessing(id: number) {
    return this.processingAccount() === id
  }
}
